import type { PatchRelease } from "../types";

export const release: PatchRelease = {
  id: "2026-06-06_01",
  version: "2026.06.06",
  date: "6 Jun 2026",
  sinceCommit: "f562060",
  gameplay: [
    { text: "Finished public games no longer appear in the online games browser." },
    {
      text: "Shop economy split: Skill Points lock in and upgrade spells, Passive Points buy stat passives, Synergy Points buy synergies; gold is for items only. +1 SP and +1 PP after every round; +1 Sy after rounds 4, 8, 12…",
    },
    {
      text: "Void debris and shooting stars added to the arena background.",
    },
  ],
  skills: [
    {
      spellId: "meteor",
      changes: [
        {
          passiveId: "armageddon",
          text: "New synergy (pairs with Fireball): casting Meteor also calls down 10 smaller meteors (42% size, 38% damage) at random spots across the arena over 5 seconds.",
        },
        {
          passiveId: "meteor_nuke",
          text: "Nuke rework (pairs with Scourge): 2× meteor size; 5s delayed shockwave (42 damage, 0.34s stun, 2× knockback); scorched ground with 3s burn at 50 DPS; shockwave travels past the arena rim into the void.",
        },
        {
          passiveId: "comet",
          text: "New synergy (pairs with Freeze): icy comet — on impact damages and freezes enemies in the blast for 3s instead of knocking them back.",
        },
      ],
    },
    {
      spellId: "scourge",
      changes: [
        {
          passiveId: "scourge_sticky_mines",
          text: "New synergy (pairs with Homing): instantly plant a chasing mine instead of channeling Scourge.",
        },
        { passiveId: "scourge_cooldown", text: "New passive **Quick Fuse**: −12% cooldown." },
      ],
    },
    {
      spellId: "voidrip",
      changes: [{
        passiveId: "voidrip_hell_rip",
        text: "New synergy (pairs with Firewall): cast a burning pentagram instead of the void tear — lines finish drawing, then scorch the arena for 5s.",
      }],
    },
    {
      spellId: "illusionist",
      changes: [{
        passiveId: "illusionist_suicide_squad",
        text: "New synergy (pairs with Scourge): spawn miniature copies (+15% speed) that rush enemies, swell to full size over 1s, then detonate with a Scourge blast.",
      }],
    },
    {
      spellId: "freeze",
      changes: [
        { passiveId: "freeze_cooldown", text: "New passive **Quick Cast**: −12% cooldown." },
        { passiveId: "freeze_deep", text: "New passive **Deep Freeze**: +0.25s root and silence." },
        {
          passiveId: "freeze_multishot",
          text: "New synergy (pairs with Illusionist): +2 side projectiles in a spread.",
        },
      ],
    },
    {
      spellId: "icelance",
      changes: [
        { passiveId: "icelance_damage", text: "New passive **Heavy Lance**: +25% damage." },
        { passiveId: "icelance_chill", text: "New passive **Deep Chill**: +0.5s slow." },
        { passiveId: "icelance_multishot", text: "New synergy (pairs with Illusionist): +2 side projectiles in a spread." },
      ],
    },
    {
      spellId: "drain",
      changes: [
        { passiveId: "drain_damage", text: "New passive **Stronger Siphon**: +25% damage." },
        { passiveId: "drain_wound", text: "New passive **Deep Wound**: +0.3s slow." },
        { passiveId: "drain_multishot", text: "New synergy (pairs with Illusionist): +2 side projectiles in a spread." },
      ],
    },
    {
      spellId: "disable",
      changes: [
        { passiveId: "disable_bind", text: "New passive **Tighter Bind**: +0.3s root." },
        { passiveId: "disable_cooldown", text: "New passive **Quick Cast**: −12% cooldown." },
        { passiveId: "disable_multishot", text: "New synergy (pairs with Illusionist): +2 side projectiles in a spread." },
      ],
    },
    {
      spellId: "link",
      changes: [
        { passiveId: "link_damage", text: "New passive **Stronger Hook**: +20% damage." },
        { passiveId: "link_leash", text: "New passive **Long Leash**: +0.4s tether." },
        { passiveId: "link_multishot", text: "New synergy (pairs with Illusionist): +2 side projectiles in a spread." },
      ],
    },
    {
      spellId: "splitter",
      changes: [
        { passiveId: "splitter_damage", text: "New passive **Heavy Core**: +20% damage." },
        { passiveId: "splitter_shards", text: "New passive **Extra Shards**: +1 shard on impact." },
        { passiveId: "splitter_multishot", text: "New synergy (pairs with Illusionist): +2 side projectiles in a spread." },
      ],
    },
    {
      spellId: "bouncer",
      changes: [
        { passiveId: "bouncer_damage", text: "New passive **Hot Bounce**: +20% damage." },
        { passiveId: "bouncer_extra", text: "New passive **Extra Ricochet**: +1 bounce." },
        { passiveId: "bouncer_multishot", text: "New synergy (pairs with Illusionist): +2 side projectiles in a spread." },
      ],
    },
    {
      spellId: "homing",
      changes: [
        { passiveId: "homing_speed", text: "New passive **Swift Seeker**: +20% projectile speed." },
        { passiveId: "homing_damage", text: "New passive **Volatile Warhead**: +20% damage." },
        { passiveId: "homing_multishot", text: "New synergy (pairs with Illusionist): +2 side projectiles in a spread." },
      ],
    },
    {
      spellId: "boomerang",
      changes: [
        { passiveId: "boomerang_speed", text: "New passive **Long Arc**: +20% projectile speed." },
        { passiveId: "boomerang_damage", text: "New passive **Sharper Edge**: +20% damage." },
        { passiveId: "boomerang_multishot", text: "New synergy (pairs with Illusionist): +2 side projectiles in a spread." },
      ],
    },
    {
      spellId: "fireball",
      changes: [{
        passiveId: "fireball_multishot",
        text: "New synergy (pairs with Illusionist): +2 side projectiles in a spread.",
      }],
    },
    {
      spellId: "swap",
      changes: [{
        passiveId: "swap_multishot",
        text: "New synergy (pairs with Illusionist): +2 side projectiles in a spread.",
      }],
    },
    {
      spellId: "sanctuary",
      changes: [
        { passiveId: "sanctuary_range", text: "New passive **Wider Grace**: +25% pulse radius." },
        { passiveId: "sanctuary_heal", text: "New passive **Stronger Mend**: +20% healing." },
      ],
    },
    {
      spellId: "shield",
      changes: [
        { passiveId: "shield_duration", text: "New passive **Steady Bubble**: +0.5s knockback immunity." },
        { passiveId: "shield_cooldown", text: "New passive **Quick Ward**: −8s cooldown." },
      ],
    },
    {
      spellId: "barrier",
      changes: [
        { passiveId: "barrier_absorb", text: "New passive **Reinforced Ward**: +20% damage absorbed." },
        { passiveId: "barrier_duration", text: "New passive **Lasting Ward**: +1s ward duration." },
      ],
    },
    {
      spellId: "reflect",
      changes: [
        { passiveId: "reflect_duration", text: "New passive **Wide Mirror**: +0.35s reflect duration." },
        { passiveId: "reflect_cooldown", text: "New passive **Quick Raise**: −12% cooldown." },
      ],
    },
    {
      spellId: "timeshift",
      changes: [
        { passiveId: "timeshift_cooldown", text: "New passive **Clean Timeline**: −12% cooldown." },
        { passiveId: "timeshift_restore", text: "New passive **Stronger Rewind**: rewind restores an extra 15% of missing HP." },
        {
          passiveId: "timeshift_bullet_time",
          text: "New synergy (pairs with Rush): arena slows to half speed; you move, cast, and fight at full speed.",
        },
      ],
    },
    {
      spellId: "blizzard",
      changes: [{
        passiveId: "blizzard_damage",
        text: "**Rime Shards** tick damage bonus increased from +20% to +40%.",
      }],
    },
    {
      spellId: "frozenorb",
      changes: [{
        passiveId: "frozenorb_void_maelstrom",
        text: "New synergy (pairs with Void Beam): unstable void orb wobbles and fires void beams in all directions while rolling.",
      }],
    },
  ],
};
